import React from "react";
import { BCMSDate, BCMSDefaultModel, DisabledPrompt, useBCMSContext, BCMSStateManagerCRUD, useStateManager, BCMSRecord, StateManager, useModel, useRoute, useWorkflow } from "bcms-react";
import Classes, { Account, OneTimeWireTransfer } from "model";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from "components/shared";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { PrimaryButton, SecondaryButton, UIAmountInput, UICheckbox, UITextInput } from "components/ui";
import DateSelect from "components/custom-ui/DateSelect";
import WirePayeeInformationWidget from "./components/WirePayeeInformationWidget";
import WireFinancialInstitutionForm from "./components/WireFinancialInstitutionForm";
import WirePayeeConfirmationWidget from "./components/WirePayeeConfirmationWidget";

import "./styles.css";
import "./mobile.css";

/**
 * @function OneTimeWirePage
 * @return {React.Component}
 * @exported true
 * @description The main component for OneTimeWirePage
 */
export default function OneTimeWirePage(props) {
    const context = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, context);
    const data = { ...getDefaultData(), ...props.data ?? {} };
    const ui = { ...getDefaultUI(), ...props.ui ?? {} };
    const { user } = useModel();

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onSubmittedConfirmationClose: () => {
            BCMSStateManagerCRUD.init(state)
            .update([ "data", "createdOneTimeWireTransfer" ], undefined)
            .update([ "data", "wireTransferSubmitted" ], false)
            .submit(setState);
        },

        onChangeOneTimeWireProp: prop => val => {
            const crud = BCMSStateManagerCRUD.init(state);

            if(prop === "date") crud.update([ "data", "didSetDate" ], true);

            if(prop === "offsetAccount") {
                const selectedOffsetAccount = Account.initByID(val);
                crud.update([ "data", "oneTimeWireTransfer", "offsetAccount" ], selectedOffsetAccount ?? undefined);
            } else {
                crud.update([ "data", "oneTimeWireTransfer", prop ], val);
            }
            
            crud.submit(setState);
        },
        onSubmitClick: () => {
            const defaultOneTimeWireTransfer = BCMSDefaultModel.oneTimeWireTransfer();
            defaultOneTimeWireTransfer.initiatedBy = `${ user.first } ${ user.last }`;
            let oneTimeWireTransferRecord = state.data.oneTimeWireTransfer.record;

            // START: Increment the reference number.
            const allOneTimeWireTransfers = OneTimeWireTransfer.initAll()
            .filter(item => item.id !== undefined)
            .sort((a, b) => parseFloat(b.referenceNumber) - parseFloat(a.referenceNumber))
            let lastReferenceNumber = defaultOneTimeWireTransfer.referenceNumber;
            if(allOneTimeWireTransfers.length > 0) {
                lastReferenceNumber = parseFloat(allOneTimeWireTransfers[0].referenceNumber) + 1;
            }
            // END: Increment the reference number.

            oneTimeWireTransferRecord = { 
                ...oneTimeWireTransferRecord, id: OneTimeWireTransfer.nextID(), referenceNumber: lastReferenceNumber 
            };
            const oneTimeWireTransfer = OneTimeWireTransfer.init(oneTimeWireTransferRecord);
            const mergeRecord = BCMSDefaultModel.mergeRecords(oneTimeWireTransfer, defaultOneTimeWireTransfer);
           
            BCMSStateManagerCRUD.init(state)
            .update([ "data", "createdOneTimeWireTransfer" ], OneTimeWireTransfer.init({ ...mergeRecord }))
            .update([ "data", "wireTransferSubmitted" ], true)
            .update([ "data", "oneTimeWireTransfer" ], getDefaultData().oneTimeWireTransfer)
            .update([ "data", "didSetDate" ], false)
            .submit(setState);

            BCMSRecord.batch([
                OneTimeWireTransfer.create({ ...mergeRecord })
            ], context, Classes);

            window.scrollTo(0, 0);
        }
    });

    return (
        <PageContainer className="one-time-wire-page" state={ { data, ui } } controller={ controller }>
            <PageContent/>
        </PageContainer>
    )
}

export const getDefaultData = () => ({
    oneTimeWireTransfer: OneTimeWireTransfer.init({
        fundingAccount: undefined,
        date: undefined,
        payeeType: "domestic",
        financialInstitutionNumberType: "aba",
        receivingInstitutionNumberType: "aba",
        intermediaryInstitutionNumberType: "aba"
    }),
    didSetDate: false,
    createdOneTimeWireTransfer: undefined,
    wireTransferSubmitted: false,
    offsetAccounts: Account.initAllAsset()
});

export const getDefaultUI = () => ({
    // Opens all the dropdowns in the page.
    /*dateSelectIsActive: true,
    offsetAccountSelectIsActive: true,
    stateSelectIsActive: true,
    countrySelectIsActive: true,
    financialInstitutionNumberTypeSelectIsActive: true,
    financialInstitutionStateSelectIsActive: true,
    financialInstitutionCountrySelectIsActive: true,
    receivingInstitutionNumberTypeSelectIsActive: true,
    receivingInstitutionStateSelectIsActive: true,
    receivingInstitutionCountrySelectIsActive: true,
    intermediaryInstitutionNumberTypeSelectIsActive: true,
    intermediaryInstitutionStateSelectIsActive: true,
    intermediaryInstitutionCountrySelectIsActive: true,*/
});

function PageContent(props) { 
    const { data, ui, onChangeOneTimeWireProp, onSubmitClick, onSubmittedConfirmationClose } = useStateManager();
    const action = {
        onChangeOneTimeWireProp,
        onSubmittedConfirmationClose
    };
    const offsetAccountSelectIsActive = ui.offsetAccountSelectIsActive;
    const offsetAccounts = data.offsetAccounts ?? [];
    const oneTimeWireTransfer = data.oneTimeWireTransfer;
    const selectedOffsetAccount = oneTimeWireTransfer.fundingAccount ?? { id: "prompt" };
    const amount = oneTimeWireTransfer.amount;
    // Past days are disabled.
    const disableDay = date => date.isBefore(BCMSDate.init());
    const offsetAccountRows = offsetAccounts.reduce((acc, item, i) => {
        if(i === 0) acc.push(<CustomSelectItem key={ "prompt" } value={ "prompt" }>- Select a Funding Account -</CustomSelectItem>);
        const accountName = `${ item.number } - ${ item.nickname } (Available ${ item.availableBalance.format() })`;
        acc.push(
            <CustomSelectItem key={ item.id } value={ item.id }>{ accountName } </CustomSelectItem>
        );
        return acc;
    }, []);
    let requiresReceivingFinancialInstitutionDetails = oneTimeWireTransfer.requiresReceivingFinancialInstitutionDetails ?? false;
    const requiresIntermediaryFinancialInstitutionDetails = oneTimeWireTransfer.requiresIntermediaryFinancialInstitutionDetails ?? false;

    let disableRequiresReceivingFinancialInstitutionDetails = false;
    if(oneTimeWireTransfer.payeeType === "international" && oneTimeWireTransfer.financialInstitutionNumberType === "aba") {
        requiresReceivingFinancialInstitutionDetails = true;
        disableRequiresReceivingFinancialInstitutionDetails = true;
    }

    return (
        <PageContentView>
            { data.wireTransferSubmitted && 
            <WirePayeeConfirmationWidget data={ data } action={ action } />
            }
                <CenterContentContainer>
                    <ContentHeaderView text="One-Time Wire" />
                    <FormContainer className="small-form-width">
                        <FormRowView text="* Funding Account">
                            <CustomSelect active={ offsetAccountSelectIsActive } value={ selectedOffsetAccount.id }
                            onChange={ onChangeOneTimeWireProp("fundingAccount") }>
                                { offsetAccountRows }
                            </CustomSelect>
                        </FormRowView>
                        <FormRowView text="* Amount">
                            <UIAmountInput value={ amount.amount } onChange={ onChangeOneTimeWireProp("amount") } />
                        </FormRowView>
                        <FormRowView text="* Date">
                            <DateSelect active={ ui.dateSelectIsActive } value={ data.didSetDate ? oneTimeWireTransfer.date : undefined } disableDay={ disableDay } onChange={ onChangeOneTimeWireProp("date") } />
                        </FormRowView>
                        <FormRowView text="Reason for Payment">
                            <UITextInput value={ oneTimeWireTransfer.reasonForPayment } onChange={ onChangeOneTimeWireProp("reasonForPayment") } />
                        </FormRowView>
                        <FormRowView text="Memo">
                            <UITextInput value={ oneTimeWireTransfer.memo } onChange={ onChangeOneTimeWireProp("memo") } />
                        </FormRowView>
                    </FormContainer>

                    <WirePayeeInformationWidget data={ data } ui={ ui } action={ action } />

                    <div className="payee-financial-institution financial-institution-container">
                        <h2 className="header-text">Payee Financial Institution</h2>
                        <WireFinancialInstitutionForm className="payee-financial-institution-form" data={ data } ui={ ui } action={ action } sectionType="financialInstitution" />
                    </div>

                    <div className="receiving-financial-institution-container financial-institution-container">
                        <h2 className="header-text">Receiving Financial Institution (optional)</h2>
                        {/* Wire financial institution form is shown if "Show Details" checkbox is checked */}
                        <UICheckbox disabled={ disableRequiresReceivingFinancialInstitutionDetails } label="Show Details" value={ requiresReceivingFinancialInstitutionDetails } onChange={ onChangeOneTimeWireProp("requiresReceivingFinancialInstitutionDetails") } />
                        { requiresReceivingFinancialInstitutionDetails &&
                        <WireFinancialInstitutionForm className="receiving-financial-institution-form" data={ data } ui={ ui } action={ action } sectionType="receivingInstitution" />
                        }
                        </div>
                    <div className="intermediary-financial-institution-container financial-institution-container">
                        <h2 className="header-text">Intermediary Financial Institution (optional)</h2>
                        {/* Wire financial institution form is shown if "Show Details" checkbox is checked */}
                        <UICheckbox label="Show Details" value={ requiresIntermediaryFinancialInstitutionDetails } onChange={ onChangeOneTimeWireProp("requiresIntermediaryFinancialInstitutionDetails") } />
                        { requiresIntermediaryFinancialInstitutionDetails && 
                        <WireFinancialInstitutionForm className="receiving-financial-institution-form" data={ data } ui={ ui } action={ action } sectionType="intermediaryInstitution" />
                        }
                    </div>

                    <div className="flex-container flex-horizontal align-center button-container">
                        <DisabledPrompt>
                            <SecondaryButton text="Cancel" />
                        </DisabledPrompt>
                        <PrimaryButton text="Submit" onClick={ onSubmitClick } />
                    </div>
                </CenterContentContainer>
            </PageContentView>
    );
}
